import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import communicationLogService from '../../../api/services/communicationLogService';
import candidateService from '../../../api/services/candidateService';
import jobService from '../../../api/services/jobService';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import Alert from '../../../components/common/Alert/Alert';
import { formatDate } from '../../../utils/stringUtils';
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from '../../../utils/formValidation';
import { API_BASE_URL } from '../../../api/config';
import {
  Mail,
  User,
  Send,
  ArrowLeft,
  Paperclip,
  FileText,
  Calendar,
  Clock,
  XCircle,
  Trash2,
  Search,
  Eye,
  Loader2,
  ChevronDown
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';

const NewCommunication = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [candidateSearchOpen, setCandidateSearchOpen] = useState(false);
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [selectedCandidateData, setSelectedCandidateData] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    candidateId: '',
    candidateIdDisplay: '', // Display candidateId (e.g., "CAND1762490960049")
    candidateName: '',
    position: '',
    offerId: '',
    communicationType: 'email', // Fixed to email only
    subject: '',
    message: '',
    recipient: '',
    priority: 'medium',
    notes: '',
    tags: []
  });

  // Load candidates - fetch all except rejected, with backend search
  const loadCandidates = async (searchTerm = '') => {
    try {
      setIsLoadingCandidates(true);


      const result = await candidateService.getCandidates({
        limit: 50,
        excludeStatus: 'rejected',
        ...(searchTerm && { search: searchTerm }),
        view: 'list',
        fields: 'summary'
      });

      if (result && (result.responseCode === 200 || result.responseCode === 201) && result.data && result.data.candidates) {

        setCandidates(result.data.candidates);
      } else {

        showError('Failed to load candidates');
        // Fallback to empty array
        setCandidates([]);
      }
    } catch (error) {

      showError('Error loading candidates');
      setCandidates([]);
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  // Load jobs/positions
  const loadJobs = async () => {
    try {

      const result = await jobService.getJobs({ page: 1, limit: 1000 });

      if (result && (result.responseCode === 200 || result.success) && result.data) {
        const jobsData = result.data?.jobs || result.data || [];

        // Filter only active jobs and extract unique positions
        const activeJobs = jobsData.filter(
          (job) => job.status === 'active' || job.status === 'published' || job.isActive === true
        );
        setJobs(activeJobs);
      } else {

        showError('Failed to load positions');
        setJobs([]);
      }
    } catch (error) {

      showError('Error loading positions');
      setJobs([]);
    }
  };

  // Load candidates and jobs on component mount
  useEffect(() => {
    loadCandidates();
    loadJobs();
  }, []);

  // Trigger backend search when candidateSearchTerm changes
  useEffect(() => {
    // Use simple debounce to avoid too many requests
    const handler = setTimeout(() => {
      loadCandidates(candidateSearchTerm.trim());
    }, 300);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateSearchTerm]);

  // Close candidate dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (candidateSearchOpen && !event.target.closest('.candidate-dropdown-container')) {
        setCandidateSearchOpen(false);
        setCandidateSearchTerm('');
      }
    };

    if (candidateSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [candidateSearchOpen]);

  // Email templates
  const emailTemplates = {
    custom: 'Custom Message',
    offer_letter: 'Job Offer Letter',
    interview_invite: 'Interview Invitation',
    rejection: 'Application Rejection',
    onboarding_info: 'Onboarding Information',
    reminder: 'Reminder'
  };

  const handleInputChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Apply validation based on field type
    if (name === 'subject') {
      // Allow requested symbols
      value = value.replace(INVALID_CHARS_REGEX, '');
      value = value.substring(0, 150);
    } else if (name === 'position') {
      // Allow requested symbols
      value = value.replace(INVALID_CHARS_REGEX, '');
    } else if (name === 'recipient') {
      // Allow only email-valid characters: alphanumeric, @, ., -, _, +
      value = value.replace(/[^a-zA-Z0-9@._+-]/g, '');
      value = value.substring(0, 100);
    } else if (name === 'message') {
      // Allow all characters except < and >
      value = value.replace(/[<>]/g, '');
      const MAX = 5000;
      value = value.substring(0, MAX);
    } else if (name === 'notes') {
      // Allow requested symbols
      value = value.replace(INVALID_CHARS_REGEX, '');
      const MAX = 1000;
      value = value.substring(0, MAX);
    } else if (name === 'offerId') {
      const MAX = 40; value = value.replace(INVALID_CHARS_REGEX, '').slice(0, MAX);
    }


    setFormData(prev => {
      const nextState = {
        ...prev,
        [name]: value
      };

      if (name === 'position' && prev.template === 'interview_invite') {
        nextState.subject = getTemplateSubject(prev.template, value);
        nextState.message = getTemplateMessage(prev.template, prev.candidateName, value);
      }

      return nextState;
    });

    // Clear error on change
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCandidateSelection = async (candidate) => {
    const candidateId = candidate?.id || candidate?._id;

    if (!candidateId) {
      setFormData(prev => ({
        ...prev,
        candidateId: '',
        candidateIdDisplay: '',
        candidateName: '',
        position: '',
        recipient: '',
        phone: '',
        offerId: ''
      }));
      setSelectedCandidateData(null);
      return;
    }

    try {
      // Fetch candidate data using candidateId

      const result = await candidateService.getCandidateById(candidateId);


      let candidateData = null;
      // Handle different response structures
      if (result && (result.responseCode === 200 || result.responseCode === 201) && result.data) {
        // Check if data has nested candidate object (result.data.candidate)
        candidateData = result.data.candidate || result.data;
      } else if (result && (result.id || result._id || result.candidateId)) {
        candidateData = result;
      } else {
        // Fallback to basic candidate object or candidates array if API fails
        candidateData =
          candidate ||
          candidates.find(c => (c.id || c._id) === candidateId);
      }

      if (!candidateData) {
        showError('Failed to fetch candidate details');
        return;
      }

      console.log('📋 Extracted candidate data (NewCommunication):', {
        candidateId: candidateData?.candidateId,
        jobTitle: candidateData?.jobTitle,
        email: candidateData?.email,
        name: candidateData?.name
      });

      const candidateName =
        candidateData?.name ||
        `${candidateData?.firstName || ''} ${candidateData?.lastName || ''}`.trim();
      const candidateJobTitle = candidateData?.jobTitle || candidateData?.position || '';

      // Try to match candidate's job title with a job in the dropdown
      // This ensures the position is selected from the dropdown options
      let matchedPosition = '';
      if (candidateJobTitle && jobs.length > 0) {
        // First try exact match (case-insensitive)
        let matchedJob = jobs.find(
          job => job.title?.toLowerCase().trim() === candidateJobTitle.toLowerCase().trim()
        );

        // If no exact match, try partial match (contains)
        if (!matchedJob) {
          matchedJob = jobs.find(
            job =>
              job.title?.toLowerCase().includes(candidateJobTitle.toLowerCase().trim()) ||
              candidateJobTitle.toLowerCase().trim().includes(job.title?.toLowerCase())
          );
        }

        if (matchedJob && matchedJob.title) {
          matchedPosition = matchedJob.title; // Use the job title from dropdown
        }
      }

      setSelectedCandidateData(candidateData);

      setFormData(prev => {
        // Always auto-fill when candidate is selected (user requirement)
        // Use matched position from dropdown, or empty if no match
        const finalPosition = matchedPosition;

        const newFormData = {
          ...prev,
          candidateId: candidateId, // MongoDB _id for API calls
          candidateIdDisplay: candidateData?.candidateId || '', // Display candidateId (e.g., "CAND1762490960049")
          candidateName: candidateName,
          position: finalPosition, // Auto-fill position from candidate's jobTitle
          recipient: candidateData?.email || '', // Auto-fill recipient email
          phone: candidateData?.phone || '',
          offerId: `OFF-${Date.now()}`
        };



        // If a template is already selected, update the message with new candidate info
        if (prev.template) {
          newFormData.subject = getTemplateSubject(prev.template, newFormData.position);
          newFormData.message = getTemplateMessage(
            prev.template,
            newFormData.candidateName,
            newFormData.position
          );
        }

        return newFormData;
      });
    } catch (error) {

      showError('Error fetching candidate details');
      // Fallback to basic candidate object or candidates array
      const fallbackCandidate =
        candidate ||
        candidates.find(c => (c.id || c._id) === candidateId);

      if (fallbackCandidate) {
        const candidateName =
          fallbackCandidate?.name ||
          `${fallbackCandidate?.firstName || ''} ${fallbackCandidate?.lastName || ''}`.trim();

        // Try to match position from candidate's job title with dropdown options
        let matchedPosition = '';
        const candidateJobTitle = fallbackCandidate?.jobTitle || fallbackCandidate?.position || '';
        if (candidateJobTitle && jobs.length > 0) {
          // First try exact match (case-insensitive)
          let matchedJob = jobs.find(
            job => job.title?.toLowerCase().trim() === candidateJobTitle.toLowerCase().trim()
          );

          // If no exact match, try partial match (contains)
          if (!matchedJob) {
            matchedJob = jobs.find(
              job =>
                job.title?.toLowerCase().includes(candidateJobTitle.toLowerCase().trim()) ||
                candidateJobTitle.toLowerCase().trim().includes(job.title?.toLowerCase())
            );
          }

          if (matchedJob && matchedJob.title) {
            matchedPosition = matchedJob.title; // Use the job title from dropdown
          }
        }

        // Always auto-fill when candidate is selected (user requirement)
        // Use matched position from dropdown, or empty if no match
        const finalPosition = matchedPosition;

        setSelectedCandidateData(fallbackCandidate);

        setFormData(prev => ({
          ...prev,
          candidateId: candidateId,
          candidateIdDisplay: fallbackCandidate?.candidateId || '', // Auto-fill candidate ID
          candidateName: candidateName,
          position: finalPosition, // Auto-fill position from candidate's jobTitle
          recipient: fallbackCandidate?.email || '', // Auto-fill recipient email
          phone: fallbackCandidate?.phone || '',
          offerId: `OFF-${Date.now()}`
        }));
      }
    }
  };

  const validateField = (name, value) => {
    let msg = '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    switch (name) {
      case 'candidateId':
        if (!value) msg = 'Candidate is required.';
        break;
      case 'position':
        if (!value || value.trim().length === 0) msg = 'Position is required.';
        break;
      case 'recipient':
        if (!value) msg = 'Recipient email is required.';
        else if (!emailRegex.test(value)) msg = 'Enter a valid email address.';
        break;
      case 'subject':
        if (!value || value.trim().length < 3) msg = 'Subject is required.';
        else if (!VALID_TEXT_REGEX.test(value.trim())) msg = 'Subject contains invalid characters.';
        else if (value.length > 150) msg = 'Subject must be ≤ 150 characters.';
        break;
      case 'message':
        if (!value || value.trim().length < 10) msg = 'Message must be at least 10 characters.';
        else if (/[<>]/.test(value)) msg = 'Message cannot contain < or > characters.';
        else if (value.length > 5000) msg = 'Message must be ≤ 5000 characters.';
        break;
      case 'offerId': {
        // We reuse INVALID_CHARS_REGEX to test if there's any invalid char
        if (value && INVALID_CHARS_REGEX.test(value)) msg = 'Offer ID contains invalid characters.';
        else if (value && value.length > 40) msg = 'Offer ID must be ≤ 40 characters.';
        break;
      }
      case 'notes':
        if (value && !VALID_TEXT_REGEX.test(value.trim())) msg = 'Notes contains invalid characters.';
        else if (value && value.length > 1000) msg = 'Notes must be ≤ 1000 characters.';
        break;
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [name]: msg }));
    return !msg;
  };

  const validateForm = () => {
    const fields = ['candidateId', 'position', 'recipient', 'subject', 'message', 'notes'];
    let ok = true;
    fields.forEach(f => { if (!validateField(f, formData[f])) ok = false; });
    return ok;
  };

  const handleTemplateChange = (template) => {
    setFormData(prev => ({
      ...prev,
      template,
      subject: getTemplateSubject(template, prev.position),
      message: getTemplateMessage(template, prev.candidateName, prev.position)
    }));
  };

  const getTemplateSubject = (template, position = '') => {
    switch (template) {
      case 'offer_letter':
        return `Job Offer - ${position || 'Position'}`;
      case 'interview_invite':
        return `Interview Invitation - ${position || 'Position'}`;
      case 'rejection':
        return `Application Update - ${position || 'Position'}`;
      case 'reminder':
        return `Reminder - ${position || 'Position'} Application`;
      case 'onboarding_info':
        return `Onboarding Information - ${position || 'Position'}`;
      default:
        return '';
    }
  };

  const getTemplateMessage = (template, candidateName = '', position = '') => {
    switch (template) {
      case 'offer_letter':
        const currentDate = formatDate(new Date(), { includeTime: false });
        return `Dear ${candidateName || 'Candidate'},

We are delighted to offer you the position of ${position || 'Position'} at our company.

Offer Details:
- Position: ${position || 'Position'}
- Start Date: ${currentDate}
- Salary: [To be discussed]

Please review the attached offer letter and let us know your decision.

Best regards,
HR Team`;
      case 'interview_invite':
        const interviewDate = formatDate(new Date(), { includeTime: false });
        const interviewTime = new Date().toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
        return `Dear ${candidateName || 'Candidate'},

Thank you for your interest in the ${position || 'Position'} position. We are pleased to invite you for an interview.

Interview Details:
- Position: ${position || 'Position'}
- Date: ${interviewDate}
- Time: ${interviewTime}
- Location: [To be confirmed]

Please confirm your availability and let us know if you have any questions.

Best regards,
HR Team`;
      case 'rejection':
        return `Dear ${candidateName || 'Candidate'},

Thank you for your interest in the ${position || 'Position'} position and for taking the time to interview with us.

After careful consideration, we have decided to move forward with another candidate for this position. However, we were impressed with your qualifications and would like to keep your resume on file for future opportunities.

We wish you the best in your job search.

Best regards,
HR Team`;
      case 'follow_up':
        return `Dear ${candidateName || 'Candidate'},

I hope this email finds you well. I wanted to follow up on your application for the ${position || 'Position'} position.

We are currently reviewing applications and will be in touch soon with next steps. Thank you for your patience.

Best regards,
HR Team`;
      case 'reminder':
        return `Dear ${candidateName || 'Candidate'},

This is a friendly reminder about your upcoming interview for the ${position || 'Position'} position.

Please ensure you have all necessary documents and are prepared for the interview.

Best regards,
HR Team`;
      case 'onboarding_info':
        return `Dear ${candidateName || 'Candidate'},

Welcome to our team! We are excited to have you join us as ${position || 'Position'}.

Please find attached the onboarding information and required documents. Please review and complete the necessary forms before your start date.

Best regards,
HR Team`;
      default:
        return '';
    }
  };

  const ensureFilenameHasExtension = (filename, mimeType) => {
    const clean = String(filename || 'file').trim();
    if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;
    const type = String(mimeType || '').toLowerCase();
    if (type === 'application/pdf') return `${clean}.pdf`;
    if (type === 'application/msword') return `${clean}.doc`;
    if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return `${clean}.docx`;
    if (type === 'text/plain') return `${clean}.txt`;
    if (type.startsWith('image/jpeg')) return `${clean}.jpg`;
    if (type.startsWith('image/png')) return `${clean}.png`;
    if (type.startsWith('image/gif')) return `${clean}.gif`;
    if (type.startsWith('image/webp')) return `${clean}.webp`;
    return clean;
  };

  const triggerBrowserDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const extractObjectKeyFromUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      // Remove leading slash from pathname
      let key = urlObj.pathname.startsWith('/') ? urlObj.pathname.slice(1) : urlObj.pathname;
      // Remove bucket name if it's part of the path (virtual-hosted-style)
      if (key.startsWith('mobiloitte-intranet-bucket/')) {
        key = key.replace('mobiloitte-intranet-bucket/', '');
      }
      return key;
    } catch {
      return '';
    }
  };

  const downloadAttachment = async (attachment) => {
    const resolvedFilename = ensureFilenameHasExtension(
      attachment.name || attachment.filename || 'file',
      attachment.type || attachment.mimetype
    );

    try {
      // Use publicId if available, otherwise extract from URL
      const publicId = attachment.publicId || extractObjectKeyFromUrl(attachment.url);

      if (!publicId) {
        throw new Error('Unable to identify attachment');
      }

      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${API_BASE_URL}/upload/cloudinary/download?public_id=${encodeURIComponent(publicId)}&filename=${encodeURIComponent(resolvedFilename)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let finalFilename = resolvedFilename;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
        if (filenameMatch && filenameMatch[1]) {
          finalFilename = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      triggerBrowserDownload(blobUrl, finalFilename);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {

      showError(`Failed to download ${resolvedFilename}`);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => processUpload(file));
    // Reset file input to allow selecting the same file again
    e.target.value = '';
  };

  const processUpload = async (file) => {
    const attachmentId = Date.now() + Math.random();

    const newAttachment = {
      id: attachmentId,
      name: file.name,
      size: file.size,
      type: file.type,
      progress: 0,
      status: 'uploading',
      url: '',
      publicId: '',
      resourceType: file.type.startsWith('image/') ? 'image' : 'raw'
    };

    setAttachments(prev => [...prev, newAttachment]);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'communication-attachments');

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE_URL}/upload/cloudinary`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('auth_token')}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setAttachments(prev => prev.map(att =>
            att.id === attachmentId ? { ...att, progress: percentComplete } : att
          ));
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          const data = response.data;
          setAttachments(prev => prev.map(att =>
            att.id === attachmentId ? {
              ...att,
              status: 'success',
              progress: 100,
              url: data.url || data.secure_url,
              publicId: data.public_id,
              resourceType: data.resource_type || att.resourceType
            } : att
          ));
        } else {
          setAttachments(prev => prev.map(att =>
            att.id === attachmentId ? { ...att, status: 'error' } : att
          ));
          showError(`Failed to upload ${file.name}`);
        }
      };

      xhr.onerror = () => {
        setAttachments(prev => prev.map(att =>
          att.id === attachmentId ? { ...att, status: 'error' } : att
        ));
        showError(`Error uploading ${file.name}`);
      };

      xhr.send(formData);
    } catch (error) {

      setAttachments(prev => prev.map(att =>
        att.id === attachmentId ? { ...att, status: 'error' } : att
      ));
    }
  };

  const removeAttachment = async (attachmentId) => {
    const attachment = attachments.find(att => att.id === attachmentId);
    if (!attachment) return;

    // Set deleting state
    setAttachments(prev => prev.map(att =>
      att.id === attachmentId ? { ...att, status: 'deleting' } : att
    ));

    // If it was already uploaded to Cloudinary, delete it from there too
    if (attachment.publicId && attachment.status === 'success') {
      try {
        const response = await fetch(`${API_BASE_URL}/upload/cloudinary?public_id=${attachment.publicId}&resource_type=${attachment.resourceType}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });

        if (!response.ok) {

          showError('Failed to delete file from server');
          // Revert status to success if deletion failed
          setAttachments(prev => prev.map(att =>
            att.id === attachmentId ? { ...att, status: 'success' } : att
          ));
          return;
        }
      } catch (error) {

        showError('Error deleting file');
        // Revert status to success
        setAttachments(prev => prev.map(att =>
          att.id === attachmentId ? { ...att, status: 'success' } : att
        ));
        return;
      }
    }

    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    files.forEach(file => processUpload(file));
  };

  // No longer needed as we upload directly
  const uploadAttachmentsToCloudinary = async () => {
    return attachments.filter(att => att.status === 'success');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate before submit
    if (!validateForm()) {
      showError('Please fix the validation errors before submitting. All required fields must be filled correctly.');
      return;
    }

    // Check if all required fields are filled (Email only - removed communicationType)
    const requiredFields = ['candidateId', 'position', 'recipient', 'subject', 'message'];
    const missingFields = requiredFields.filter(field => {
      return !formData[field] || formData[field].toString().trim() === '';
    });

    if (missingFields.length > 0) {
      showError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    setIsLoading(true);

    try {
      console.log('📤 Creating communication with data:', formData);
      console.log('📎 Attachments data:', attachments);

      // Prepare data for API
      // Verify candidate has job title (position) in DB
      let candidateDetails = candidates.find(c => (c.id || c._id) === formData.candidateId);
      try {
        if (!candidateDetails || (!candidateDetails.position && !candidateDetails.jobTitle)) {
          const fresh = await candidateService.getCandidateById(formData.candidateId);
          candidateDetails = fresh?.data || fresh || candidateDetails;
        }
      } catch (fetchErr) {
        console.log('Could not fetch candidate details, using selected list item');
      }
      const candidatePosition = (candidateDetails?.position || candidateDetails?.jobTitle || '').trim();
      if (!candidatePosition) {
        showError('Selected candidate has no job title in database. Please update candidate profile with a job title and try again.');
        setIsLoading(false);
        return;
      }

      // Upload all attachments to Cloudinary before sending
      const uploadedAttachments = await uploadAttachmentsToCloudinary();

      const communicationData = {
        candidateId: formData.candidateId,
        offerId: formData.offerId || `OFF-${Date.now()}`,
        communicationType: formData.communicationType,
        subject: formData.subject,
        message: formData.message,
        recipient: formData.recipient,
        priority: formData.priority,
        notes: formData.notes,
        tags: formData.tags,
        // Optional helpful context
        candidateName: formData.candidateName,
        // Note: backend derives jobTitle from Candidate.position; not sending here
        // Include attachments (after upload)
        attachments: uploadedAttachments.length > 0 ? uploadedAttachments
          .filter(att => !!att.url && att.url.trim().length > 0)
          .map(att => ({
            name: att.name || '',
            url: att.url,
            size: att.size || 0,
            type: att.type || ''
          })) : []
      };





      const result = await communicationLogService.createCommunicationLog(communicationData);

      if (result && (result.responseCode === 200 || result.responseCode === 201)) {

        showSuccess('Communication created successfully');
        setTimeout(() => {
          navigate('/offer_communication_logs');
        }, 2000);
      } else {

        showError('Failed to create communication');
      }
    } catch (error) {

      showError('Error creating communication');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className="mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleCancel}
          >
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">New Communication</h1>
            <p className="text-gray-600">Create a new communication for candidates</p>
          </div>
        </div>
      </div>

      {/* Communication Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-6">Communication Details</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Template and Select Candidate */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="template" className="block text-sm font-medium text-gray-700 mb-2">
                Email Template
              </label>
              <select
                id="template"
                name="template"
                value={formData.template}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              >
                {Object.entries(emailTemplates).map(([key, value]) => (
                  <option key={key} value={key}>{value}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="candidateId" className="block text-sm font-medium text-gray-700 mb-2">
                Select Candidate <span className="text-red-500">*</span>
              </label>
              <div className="relative candidate-dropdown-container">
                {/* Display selected candidate or placeholder */}
                <div
                  onClick={() => {
                    setCandidateSearchOpen(!candidateSearchOpen);
                    if (!candidateSearchOpen) {
                      setCandidateSearchTerm('');
                    }
                  }}
                  className={`mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 bg-white flex items-start justify-between gap-3 cursor-pointer hover:border-gray-400 focus-within:border-brand focus-within:ring-1 focus-within:ring-brand`}
                >
                  <span
                    className={`${selectedCandidateData ? 'text-gray-900' : 'text-gray-500'
                      } flex-1 min-w-0 whitespace-normal break-words`}
                  >
                    {selectedCandidateData
                      ? `${selectedCandidateData.firstName || ''} ${selectedCandidateData.lastName || ''
                      }${selectedCandidateData.email ? ` (${selectedCandidateData.email})` : ''}`
                      : 'Search and select candidate from hired list'}
                  </span>
                  <div className="flex-shrink-0 pt-1">
                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 transition-transform ${candidateSearchOpen ? 'transform rotate-180' : ''
                        }`}
                    />
                  </div>
                </div>

                {/* Dropdown with search */}
                {candidateSearchOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    {/* Search input */}
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search by candidate name or email..."
                          value={candidateSearchTerm}
                          onChange={(e) => {
                            // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                            const value = e.target.value
                              .replace(/[^a-zA-Z0-9\s@._+-]/g, '')
                              .slice(0, 100);
                            setCandidateSearchTerm(value);
                          }}
                          onKeyPress={(e) => {
                            // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                            if (
                              !/^[a-zA-Z0-9\s@._+-]$/.test(e.key) &&
                              ![
                                'Backspace',
                                'Delete',
                                'Tab',
                                'Enter',
                                'ArrowLeft',
                                'ArrowRight'
                              ].includes(e.key)
                            ) {
                              e.preventDefault();
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          maxLength={100}
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand text-sm"
                          autoFocus
                        />
                      </div>
                    </div>

                    {/* Results list */}
                    <div className="max-h-64 overflow-y-auto">
                      {isLoadingCandidates ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          Searching candidates...
                        </div>
                      ) : !candidates || candidates.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          No candidates found. Try a different search term.
                        </div>
                      ) : (
                        candidates
                          .slice(0, 50)
                          .map((candidate) => (
                            <div
                              key={candidate.id || candidate._id}
                              onClick={async () => {
                                const candidateId = candidate.id || candidate._id;
                                if (!candidateId) {
                                  showError(
                                    'Invalid candidate information. Please select a different candidate.'
                                  );
                                  return;
                                }

                                // Close dropdown immediately
                                setCandidateSearchOpen(false);
                                setCandidateSearchTerm('');

                                await handleCandidateSelection(candidate);
                              }}
                              className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                            >
                              <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-900">
                                  {candidate.name ||
                                    `${candidate.firstName || ''} ${candidate.lastName || ''
                                      }`.trim()}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {candidate.email || 'No email'}
                                </span>
                                {candidate.jobTitle && (
                                  <span className="text-xs text-gray-400">
                                    {candidate.jobTitle}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              {errors.candidateId && (
                <p className="text-sm text-red-600 mt-1">{errors.candidateId}</p>
              )}
            </div>
          </div>

          {/* Candidate ID and Position */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="candidateIdDisplay" className="block text-sm font-medium text-gray-700 mb-2">
                Candidate ID
              </label>
              <input
                type="text"
                id="candidateIdDisplay"
                name="candidateIdDisplay"
                value={formData.candidateIdDisplay || ''}
                readOnly
                placeholder="Select a candidate to see ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              />
            </div>

            <div>
              <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
                Position <span className="text-red-500">*</span>
              </label>
              <select
                id="position"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                onBlur={(e) => validateField('position', e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              >
                <option value="">Select a position</option>
                {jobs.map(job => (
                  <option key={job._id || job.id} value={job.title}>
                    {job.title}
                  </option>
                ))}
              </select>
              {errors.position && (
                <p className="text-sm text-red-600 mt-1">{errors.position}</p>
              )}
            </div>
          </div>

          {/* Recipient Email and Subject */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="recipient" className="block text-sm font-medium text-gray-700 mb-2">
                Recipient Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="recipient"
                name="recipient"
                value={formData.recipient}
                onChange={handleInputChange}
                onBlur={(e) => validateField('recipient', e.target.value)}
                required
                placeholder="Enter recipient email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              />
              {errors.recipient && (
                <p className="text-sm text-red-600 mt-1">{errors.recipient}</p>
              )}
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                onBlur={(e) => validateField('subject', e.target.value)}
                maxLength={150}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              />
              <div className="flex justify-between items-center mt-1">
                {errors.subject && (
                  <p className="text-sm text-red-600">{errors.subject}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.subject?.length || 0}/150 characters
                </p>
              </div>
            </div>
          </div>

          {/* Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div></div>
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              onKeyPress={(e) => {
                // Allow all characters except < and >
                if (e.key === '<' || e.key === '>') {
                  e.preventDefault();
                }
              }}
              onBlur={(e) => validateField('message', e.target.value)}
              maxLength={5000}
              required
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            />
            <div className="flex justify-between items-center mt-1">
              {errors.message && (
                <p className="text-sm text-red-600">{errors.message}</p>
              )}
              <p className="text-xs text-gray-500 ml-auto">
                {formData.message?.length || 0}/5000 characters
              </p>
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attachments
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-6 transition-colors ${isDragging ? 'border-brand bg-brand/5' : 'border-gray-300 hover:border-gray-400'
                }`}
            >
              <div className="text-center">
                <Paperclip className={`h-12 w-12 mx-auto mb-4 transition-colors ${isDragging ? 'text-brand' : 'text-gray-400'}`} />
                <div className="flex justify-center">
                  <input
                    type="file"
                    id="attachments"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <p className="text-sm text-gray-500">
                    <label
                      htmlFor="attachments"
                      className="cursor-pointer text-brand font-semibold"
                    >
                      Choose Files
                    </label>
                    {' '}or drag and drop
                  </p>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Upload documents, images, or other files (Max 25MB)
                </p>
              </div>
            </div>

            {/* Attachment List */}
            {attachments.length > 0 && (
              <div className="mt-4 space-y-3">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3 overflow-hidden">
                        <FileText className={`h-5 w-5 flex-shrink-0 ${attachment.status === 'error' ? 'text-red-400' : 'text-gray-400'}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{attachment.name}</p>
                          <p className="text-xs text-gray-500">
                            {(attachment.size / 1024 / 1024).toFixed(2)} MB • {attachment.status}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {attachment.status === 'success' && attachment.url && (
                          <button
                            type="button"
                            onClick={() => downloadAttachment(attachment)}
                            className="p-1.5 text-[#0F2942] hover:bg-blue-50 rounded-md transition-colors"
                            title="Download File"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeAttachment(attachment.id)}
                          disabled={attachment.status === 'deleting'}
                          className={`p-1.5 rounded-md transition-colors ${attachment.status === 'deleting'
                            ? 'text-gray-400 bg-gray-50'
                            : 'text-red-600 hover:bg-red-50'
                            }`}
                          title="Remove File"
                        >
                          {attachment.status === 'deleting' ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    {attachment.status === 'uploading' && (
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div
                          className="bg-brand h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${attachment.progress}%` }}
                        ></div>
                      </div>
                    )}
                    {attachment.status === 'error' && (
                      <p className="text-[10px] text-red-500 font-medium">Upload failed. Please try again.</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Additional Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              onBlur={(e) => validateField('notes', e.target.value)}
              maxLength={3000}
              rows={3}
              placeholder="Add any additional notes..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            />
            <div className="flex justify-between items-center mt-1">
              {errors.notes && (
                <p className="text-sm text-red-600">{errors.notes}</p>
              )}
              <p className="text-xs text-gray-500 ml-auto">
                {formData.notes?.length || 0}/3000 characters
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <Button
              type="button"
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="px-6 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors"
              icon={<Send className="h-4 w-4" />}
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Communication'}
            </Button>
          </div>
        </form>
      </div>

      {/* Alert Component */}
      <Alert
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        showCancel={alertState.showCancel}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm || hideAlert}
        onCancel={hideAlert}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />
    </div>
  );
};

export default NewCommunication;